// Stampa di tutti i pezzi di un cartamodello su fogli A0 (84.1 × 118.9 cm)
// con bin-packing First-Fit-Decreasing-Height per minimizzare il numero
// di fogli. Pezzi che eccedono la dimensione di un singolo A0 vengono
// posti su un foglio singolo dedicato (con avviso "fuori formato").

import type { ComputedPiece } from '../../geometry/computeGarment'
import { categorizeSegment } from '../../geometry/segmentCategory'

const A0_W = 84.1
const A0_H = 118.9
const SHEET_MARGIN = 1.0       // bordo interno del foglio
const PIECE_GUTTER = 1.5       // spazio tra pezzi (canale di taglio)
const LABEL_HEIGHT = 1.2       // spazio per l'etichetta sopra il pezzo
const PIECE_PADDING = 0.6      // padding nel viewBox SVG (per non tagliare etichette punti)

interface PointXY { x: number; y: number }

interface Bbox {
  minX: number
  minY: number
  width: number
  height: number
}

interface Placement {
  piece: ComputedPiece
  bbox: Bbox
  sheet: number
  x: number  // top-left della bbox rispetto all'area utile (cm)
  y: number
}

interface PackResult {
  placements: Placement[]
  sheetCount: number
  oversize: { piece: ComputedPiece; bbox: Bbox }[]
}

function computeBbox(piece: ComputedPiece): Bbox | null {
  if (piece.points.length === 0) return null
  const pointMap = new Map<string, PointXY>()
  for (const p of piece.points) pointMap.set(p.label, { x: p.x, y: p.y })

  const coords: PointXY[] = [...piece.points.map(p => ({ x: p.x, y: p.y }))]
  for (const seg of piece.segments) {
    for (const v of seg.viaPoints) {
      const vp = pointMap.get(v)
      if (vp) coords.push(vp)
    }
  }
  if (coords.length === 0) return null

  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
  for (const c of coords) {
    if (c.x < minX) minX = c.x
    if (c.y < minY) minY = c.y
    if (c.x > maxX) maxX = c.x
    if (c.y > maxY) maxY = c.y
  }
  return { minX, minY, width: maxX - minX, height: maxY - minY }
}

// First-Fit Decreasing Height shelf packing. Senza rotazione (drittofilo
// verticale è importante in sartoria).
function packPieces(pieces: ComputedPiece[]): PackResult {
  const useW = A0_W - 2 * SHEET_MARGIN
  const useH = A0_H - 2 * SHEET_MARGIN

  interface Sized {
    piece: ComputedPiece
    bbox: Bbox
    packW: number  // bbox.width + gutter
    packH: number  // bbox.height + label + gutter
  }
  const sized: Sized[] = []
  const oversize: PackResult['oversize'] = []

  for (const p of pieces) {
    const b = computeBbox(p)
    if (!b) continue
    const packW = b.width + PIECE_GUTTER
    const packH = b.height + LABEL_HEIGHT + PIECE_GUTTER
    if (packW > useW || packH > useH) {
      oversize.push({ piece: p, bbox: b })
      continue
    }
    sized.push({ piece: p, bbox: b, packW, packH })
  }

  // FFDH: ordina per altezza decrescente.
  sized.sort((a, b) => b.packH - a.packH)

  interface Shelf { y: number; h: number; nextX: number }
  interface Sheet { shelves: Shelf[]; totalH: number }
  const sheets: Sheet[] = []
  const placements: Placement[] = []

  for (const s of sized) {
    let done = false
    for (let si = 0; si < sheets.length && !done; si++) {
      const sh = sheets[si]
      for (const shelf of sh.shelves) {
        if (shelf.h >= s.packH && shelf.nextX + s.packW <= useW) {
          placements.push({
            piece: s.piece,
            bbox: s.bbox,
            sheet: si,
            x: shelf.nextX,
            y: shelf.y,
          })
          shelf.nextX += s.packW
          done = true
          break
        }
      }
      if (done) break
      if (sh.totalH + s.packH <= useH) {
        const newShelf: Shelf = { y: sh.totalH, h: s.packH, nextX: s.packW }
        sh.shelves.push(newShelf)
        placements.push({
          piece: s.piece,
          bbox: s.bbox,
          sheet: si,
          x: 0,
          y: sh.totalH,
        })
        sh.totalH += s.packH
        done = true
      }
    }
    if (!done) {
      sheets.push({
        shelves: [{ y: 0, h: s.packH, nextX: s.packW }],
        totalH: s.packH,
      })
      placements.push({
        piece: s.piece,
        bbox: s.bbox,
        sheet: sheets.length - 1,
        x: 0,
        y: 0,
      })
    }
  }

  return { placements, sheetCount: sheets.length, oversize }
}

function escapeXml(s: string): string {
  return s.replace(/[&<>"']/g, ch => {
    switch (ch) {
      case '&': return '&amp;'
      case '<': return '&lt;'
      case '>': return '&gt;'
      case '"': return '&quot;'
      default:  return '&#39;'
    }
  })
}

function quadraticBezierLength(p0: PointXY, p1: PointXY, p2: PointXY): number {
  const N = 32
  let total = 0
  let prev = p0
  for (let i = 1; i <= N; i++) {
    const t = i / N
    const u = 1 - t
    const x = u * u * p0.x + 2 * u * t * p1.x + t * t * p2.x
    const y = u * u * p0.y + 2 * u * t * p1.y + t * t * p2.y
    total += Math.hypot(x - prev.x, y - prev.y)
    prev = { x, y }
  }
  return total
}

function segmentLengthCm(
  from: PointXY,
  to: PointXY,
  via: PointXY[],
  type: 'line' | 'curve' | 'arc',
): number {
  if (type === 'line' || via.length === 0) {
    return Math.hypot(to.x - from.x, to.y - from.y)
  }
  if (via.length === 1) {
    const v = via[0]
    const cx = 2 * v.x - 0.5 * from.x - 0.5 * to.x
    const cy = 2 * v.y - 0.5 * from.y - 0.5 * to.y
    return quadraticBezierLength(from, { x: cx, y: cy }, to)
  }
  const all = [from, ...via, to]
  let total = 0
  for (let i = 0; i < all.length - 1; i++) {
    total += Math.hypot(all[i + 1].x - all[i].x, all[i + 1].y - all[i].y)
  }
  return total
}

function segmentMidpoint(
  from: PointXY,
  to: PointXY,
  via: PointXY[],
  type: 'line' | 'curve' | 'arc',
): PointXY {
  if (type === 'line' || via.length === 0) {
    return { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 }
  }
  if (via.length === 1) {
    const v = via[0]
    const cx = 2 * v.x - 0.5 * from.x - 0.5 * to.x
    const cy = 2 * v.y - 0.5 * from.y - 0.5 * to.y
    return {
      x: 0.25 * from.x + 0.5 * cx + 0.25 * to.x,
      y: 0.25 * from.y + 0.5 * cy + 0.25 * to.y,
    }
  }
  return via[Math.floor(via.length / 2)]
}

// Costruisce il markup interno (<g class="segments">…<g class="points">…)
// del pezzo. refSize controlla la dimensione di pallini ed etichette in cm
// — deve essere il riferimento *del pezzo intero* anche quando il viewBox
// è ristretto a una sola tile, altrimenti i pallini cambiano dimensione
// tra tile.
//
// Se `tile` è fornito, le etichette di misura vengono posizionate al
// midpoint del tratto interno alla tile (con un margine di sicurezza dal
// bordo) — così non finiscono mai fuori dal foglio. Senza tile, usano il
// midpoint geometrico del segmento intero.
function buildPieceSvgInner(
  piece: ComputedPiece,
  refSize: number,
  tile?: Rect,
): string {
  const pointMap = new Map<string, PointXY>()
  for (const p of piece.points) pointMap.set(p.label, { x: p.x, y: p.y })

  const dotR = Math.max(0.18, refSize * 0.004)
  const labelFontSize = Math.max(1.2, refSize * 0.018)
  const lengthFontSize = labelFontSize * 0.85
  const labelOffset = dotR * 2.0

  // Inset per il clamp dell'etichetta dentro la tile: metà testo in
  // orizzontale (~"00.0" = 4 caratteri ≈ 1.5em) + metà altezza in verticale.
  const insetX = lengthFontSize * 1.6
  const insetY = lengthFontSize * 1.0

  const segParts: string[] = []
  const lenParts: string[] = []
  for (const seg of piece.segments) {
    const from = pointMap.get(seg.from)
    const to = pointMap.get(seg.to)
    if (!from || !to) continue
    const via: PointXY[] = []
    for (const v of seg.viaPoints) {
      const vp = pointMap.get(v)
      if (vp) via.push(vp)
    }
    const kind = categorizeSegment(seg.notes)
    const cls = `kind-${kind}`
    if (seg.type === 'line' || via.length === 0) {
      segParts.push(`<line x1="${from.x}" y1="${from.y}" x2="${to.x}" y2="${to.y}" class="${cls}"/>`)
    } else if (via.length === 1) {
      const v = via[0]
      const cx = 2 * v.x - 0.5 * from.x - 0.5 * to.x
      const cy = 2 * v.y - 0.5 * from.y - 0.5 * to.y
      segParts.push(`<path d="M ${from.x} ${from.y} Q ${cx} ${cy} ${to.x} ${to.y}" class="${cls}"/>`)
    } else {
      const all = [from, ...via, to]
      const d = all.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')
      segParts.push(`<path d="${d}" class="${cls}"/>`)
    }
    // Linee di riferimento e pieghe non hanno utilità di misura nello
    // stampato — non sono linee da cucire/tagliare.
    if (kind === 'reference' || kind === 'fold') continue
    const len = segmentLengthCm(from, to, via, seg.type)
    if (len < 0.5) continue
    let labelPos: PointXY | null
    if (tile) {
      // 1. midpoint del tratto effettivo nella tile (così segmenti che
      //    crossano più tile prendono ognuno un'etichetta dentro al loro
      //    pezzo di tile, non al midpoint globale che potrebbe essere
      //    altrove);
      // 2. clamp del midpoint dentro un'area inset rispetto al bordo
      //    tile, così il testo non viene tagliato dai bordi del foglio.
      const mid = labelPositionInRect(from, to, via, seg.type, tile)
      if (!mid) {
        labelPos = null
      } else {
        labelPos = {
          x: Math.max(tile.x + insetX, Math.min(tile.x + tile.w - insetX, mid.x)),
          y: Math.max(tile.y + insetY, Math.min(tile.y + tile.h - insetY, mid.y)),
        }
      }
    } else {
      labelPos = segmentMidpoint(from, to, via, seg.type)
    }
    if (!labelPos) continue
    lenParts.push(
      `<text x="${labelPos.x}" y="${labelPos.y}" class="segment-length" style="font-size:${lengthFontSize}px;stroke-width:${lengthFontSize * 0.22}px">${len.toFixed(1)}</text>`,
    )
  }

  const ptParts: string[] = []
  for (const p of piece.points) {
    ptParts.push(
      `<circle cx="${p.x}" cy="${p.y}" r="${dotR}" class="point-dot"/>`,
      `<text x="${p.x}" y="${p.y}" dx="${labelOffset}" dy="${-labelOffset}" class="point-label" style="font-size:${labelFontSize}px;stroke-width:${labelFontSize * 0.25}px">${escapeXml(p.label)}</text>`,
    )
  }

  return `<g class="segments">${segParts.join('')}</g><g class="segment-lengths">${lenParts.join('')}</g><g class="points">${ptParts.join('')}</g>`
}

function buildPieceSvgString(piece: ComputedPiece, bbox: Bbox): string {
  const pad = PIECE_PADDING
  const vbX = bbox.minX - pad
  const vbY = bbox.minY - pad
  const vbW = bbox.width + 2 * pad
  const vbH = bbox.height + 2 * pad
  const refSize = Math.max(vbW, vbH, 1)
  const inner = buildPieceSvgInner(piece, refSize)
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${vbX} ${vbY} ${vbW} ${vbH}" width="${vbW}cm" height="${vbH}cm" preserveAspectRatio="xMinYMin meet">${inner}</svg>`
}

// SVG di una tile (porzione del pezzo) con dimensioni fisse useW × useH cm.
// La viewBox seleziona la finestra (in coordinate del pezzo) di questa tile.
function buildPieceTileSvg(
  piece: ComputedPiece,
  bbox: Bbox,
  vbX: number,
  vbY: number,
  vbW: number,
  vbH: number,
): string {
  const refSize = Math.max(bbox.width, bbox.height, 1)
  const inner = buildPieceSvgInner(piece, refSize, { x: vbX, y: vbY, w: vbW, h: vbH })
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${vbX} ${vbY} ${vbW} ${vbH}" width="${vbW}cm" height="${vbH}cm" preserveAspectRatio="xMinYMin meet">${inner}</svg>`
}

interface Rect { x: number; y: number; w: number; h: number }

// Clip un segmento rettilineo P1→P2 contro un rettangolo (Liang-Barsky).
// Restituisce il segmento clippato, o null se non interseca.
function clipLineToRect(p1: PointXY, p2: PointXY, r: Rect): { a: PointXY; b: PointXY } | null {
  const dx = p2.x - p1.x
  const dy = p2.y - p1.y
  let tEnter = 0
  let tExit = 1

  const clip = (p: number, q: number): boolean => {
    if (Math.abs(p) < 1e-9) return q >= 0
    const t = q / p
    if (p < 0) {
      if (t > tExit) return false
      if (t > tEnter) tEnter = t
    } else {
      if (t < tEnter) return false
      if (t < tExit) tExit = t
    }
    return true
  }

  if (!clip(-dx, p1.x - r.x)) return null
  if (!clip(dx, r.x + r.w - p1.x)) return null
  if (!clip(-dy, p1.y - r.y)) return null
  if (!clip(dy, r.y + r.h - p1.y)) return null
  if (tEnter > tExit) return null

  return {
    a: { x: p1.x + tEnter * dx, y: p1.y + tEnter * dy },
    b: { x: p1.x + tExit * dx, y: p1.y + tExit * dy },
  }
}

function lineSegmentIntersectsRect(p1: PointXY, p2: PointXY, r: Rect): boolean {
  return clipLineToRect(p1, p2, r) !== null
}

// Campiona una Bezier quadratica e restituisce il campione mediano che
// cade dentro il rettangolo (o null se nessuno).
function curveMidSampleInRect(
  p0: PointXY,
  p1: PointXY,
  p2: PointXY,
  r: Rect,
  N = 64,
): PointXY | null {
  const inside: PointXY[] = []
  for (let i = 0; i <= N; i++) {
    const t = i / N
    const u = 1 - t
    const x = u * u * p0.x + 2 * u * t * p1.x + t * t * p2.x
    const y = u * u * p0.y + 2 * u * t * p1.y + t * t * p2.y
    if (x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h) {
      inside.push({ x, y })
    }
  }
  if (inside.length === 0) return null
  return inside[Math.floor(inside.length / 2)]
}

// Una tile è "viva" se almeno un punto o l'intersezione vera di un
// segmento con la tile è non vuota.
function tileHasContent(
  piece: ComputedPiece,
  tileX: number,
  tileY: number,
  tileW: number,
  tileH: number,
): boolean {
  const r: Rect = { x: tileX, y: tileY, w: tileW, h: tileH }
  const pointMap = new Map<string, PointXY>()
  for (const p of piece.points) pointMap.set(p.label, { x: p.x, y: p.y })

  for (const p of piece.points) {
    if (p.x >= r.x && p.x <= r.x + r.w && p.y >= r.y && p.y <= r.y + r.h) return true
  }

  for (const seg of piece.segments) {
    const from = pointMap.get(seg.from)
    const to = pointMap.get(seg.to)
    if (!from || !to) continue
    const via: PointXY[] = []
    for (const v of seg.viaPoints) {
      const vp = pointMap.get(v)
      if (vp) via.push(vp)
    }
    if (seg.type === 'line' || via.length === 0) {
      if (lineSegmentIntersectsRect(from, to, r)) return true
    } else if (via.length === 1) {
      const v = via[0]
      const cx = 2 * v.x - 0.5 * from.x - 0.5 * to.x
      const cy = 2 * v.y - 0.5 * from.y - 0.5 * to.y
      if (curveMidSampleInRect(from, { x: cx, y: cy }, to, r) !== null) return true
    } else {
      const all = [from, ...via, to]
      for (let i = 0; i < all.length - 1; i++) {
        if (lineSegmentIntersectsRect(all[i], all[i + 1], r)) return true
      }
    }
  }
  return false
}

// Trova un punto interno al rect dove posizionare l'etichetta della
// misura: midpoint del tratto di segmento che effettivamente cade nel
// rect. Restituisce null se il segmento non passa nel rect.
function labelPositionInRect(
  from: PointXY,
  to: PointXY,
  via: PointXY[],
  type: 'line' | 'curve' | 'arc',
  area: Rect,
): PointXY | null {
  if (type === 'line' || via.length === 0) {
    const clipped = clipLineToRect(from, to, area)
    if (!clipped) return null
    return { x: (clipped.a.x + clipped.b.x) / 2, y: (clipped.a.y + clipped.b.y) / 2 }
  }
  if (via.length === 1) {
    const v = via[0]
    const cx = 2 * v.x - 0.5 * from.x - 0.5 * to.x
    const cy = 2 * v.y - 0.5 * from.y - 0.5 * to.y
    return curveMidSampleInRect(from, { x: cx, y: cy }, to, area)
  }
  const all = [from, ...via, to]
  for (let i = 0; i < all.length - 1; i++) {
    const clipped = clipLineToRect(all[i], all[i + 1], area)
    if (clipped) {
      return { x: (clipped.a.x + clipped.b.x) / 2, y: (clipped.a.y + clipped.b.y) / 2 }
    }
  }
  return null
}

function openHtmlInNewTab(html: string) {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const w = window.open(url, '_blank')
  if (!w) {
    URL.revokeObjectURL(url)
    alert('La nuova finestra è stata bloccata. Abilita i popup per questo sito e riprova.')
    return
  }
  setTimeout(() => URL.revokeObjectURL(url), 30_000)
}

export function printPiecesA0(pieces: ComputedPiece[], projectName: string) {
  const valid = pieces.filter(p => p.points.length > 0)
  if (valid.length === 0) {
    alert('Nessun pezzo da stampare.')
    return
  }

  const { placements, sheetCount, oversize } = packPieces(valid)

  // Per ogni pezzo "oversize", calcola la griglia di A0 necessaria per
  // contenerlo a misura 1:1. Le tile completamente vuote vengono omesse
  // dalla stampa (ma la griglia di codici A1/A2/B1/B2 rimane coerente).
  const useW = A0_W - 2 * SHEET_MARGIN
  const useH = A0_H - 2 * SHEET_MARGIN
  const tiledOversize = oversize.map(o => {
    const cols = Math.max(1, Math.ceil(o.bbox.width / useW))
    const rows = Math.max(1, Math.ceil(o.bbox.height / useH))
    const liveTiles: { row: number; col: number }[] = []
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const tileX = o.bbox.minX + c * useW
        const tileY = o.bbox.minY + r * useH
        if (tileHasContent(o.piece, tileX, tileY, useW, useH)) {
          liveTiles.push({ row: r, col: c })
        }
      }
    }
    return { ...o, cols, rows, liveTiles }
  })
  const oversizeSheetCount = tiledOversize.reduce((acc, o) => acc + o.liveTiles.length, 0)
  const totalSheets = sheetCount + oversizeSheetCount

  if (totalSheets === 0) {
    alert('Nessun pezzo con dati validi.')
    return
  }

  const bySheet = new Map<number, Placement[]>()
  for (const pl of placements) {
    const arr = bySheet.get(pl.sheet) ?? []
    arr.push(pl)
    bySheet.set(pl.sheet, arr)
  }

  const sheetsHtml: string[] = []
  let sheetIdx = 0

  for (let si = 0; si < sheetCount; si++) {
    sheetIdx++
    const placed = bySheet.get(si) ?? []
    const piecesHtml = placed.map(pl => {
      const labelX = SHEET_MARGIN + pl.x
      const labelY = SHEET_MARGIN + pl.y
      const labelW = Math.max(pl.bbox.width, 6)
      const svgX = SHEET_MARGIN + pl.x - PIECE_PADDING
      const svgY = SHEET_MARGIN + pl.y + LABEL_HEIGHT - PIECE_PADDING
      const svgStr = buildPieceSvgString(pl.piece, pl.bbox)
      return `
        <div class="piece-label" style="left:${labelX}cm;top:${labelY}cm;width:${labelW}cm;height:${LABEL_HEIGHT}cm">
          ${escapeXml(pl.piece.italianLabel)} <span class="muted">${pl.bbox.width.toFixed(1)}×${pl.bbox.height.toFixed(1)} cm</span>
        </div>
        <div class="piece-on-sheet" style="left:${svgX}cm;top:${svgY}cm">${svgStr}</div>`
    }).join('')

    sheetsHtml.push(`
      <div class="sheet-wrap">
        <div class="sheet">
          <div class="sheet-info">
            <strong>Foglio ${sheetIdx}/${totalSheets}</strong>
            <span>${escapeXml(projectName)}</span>
            <span class="meta">A0 — 84.1 × 118.9 cm · ${placed.length} ${placed.length === 1 ? 'pezzo' : 'pezzi'}</span>
          </div>
          <div class="crop tl"></div><div class="crop tr"></div>
          <div class="crop bl"></div><div class="crop br"></div>
          ${piecesHtml}
        </div>
      </div>`)
  }

  // Pezzi oversize: tassellati su più A0 con codici di assemblaggio.
  // Le tile vuote vengono omesse — i codici (A1, A2, B1…) mantengono il
  // riferimento alla griglia originale così che lo stampatore sappia dove
  // posizionare ogni foglio (es. se manca B2 in una griglia 2×2 vuol dire
  // che quell'angolo è tutto vuoto).
  for (const ov of tiledOversize) {
    const skipped = ov.cols * ov.rows - ov.liveTiles.length
    for (const { row, col } of ov.liveTiles) {
      sheetIdx++
      const tileVbX = ov.bbox.minX + col * useW
      const tileVbY = ov.bbox.minY + row * useH
      const tileSvg = buildPieceTileSvg(ov.piece, ov.bbox, tileVbX, tileVbY, useW, useH)
      const rowCode = String.fromCharCode(65 + row)
      const colCode = col + 1
      const skipNote = skipped > 0
        ? ` · ${skipped} tile vuot${skipped === 1 ? 'a' : 'e'} omess${skipped === 1 ? 'a' : 'e'}`
        : ''
      sheetsHtml.push(`
        <div class="sheet-wrap">
          <div class="sheet">
            <div class="sheet-info">
              <strong>Foglio ${sheetIdx}/${totalSheets} · ${rowCode}${colCode}</strong>
              <span>${escapeXml(projectName)}</span>
              <span class="meta">${escapeXml(ov.piece.italianLabel)} · griglia ${ov.rows}×${ov.cols} (totale ${ov.bbox.width.toFixed(1)}×${ov.bbox.height.toFixed(1)} cm)${skipNote}</span>
            </div>
            <div class="crop tl"></div><div class="crop tr"></div>
            <div class="crop bl"></div><div class="crop br"></div>
            <div class="piece-on-sheet" style="left:${SHEET_MARGIN}cm;top:${SHEET_MARGIN}cm">${tileSvg}</div>
          </div>
        </div>`)
    }
  }

  const html = `<!DOCTYPE html>
<html lang="it">
<head>
<meta charset="UTF-8">
<title>${escapeXml(projectName)} — Stampa A0 (${totalSheets} fogli)</title>
<style>
  @page { size: 84.1cm 118.9cm; margin: 0; }
  html, body { margin: 0; padding: 0; background: white; color: #222; font-family: system-ui, sans-serif; }
  .sheet {
    position: relative;
    width: 84.1cm; height: 118.9cm;
    box-sizing: border-box;
    overflow: hidden;
    background: white;
  }
  .sheet-info {
    position: absolute;
    top: 0.3cm; left: 0.3cm; right: 0.3cm;
    display: flex; gap: 0.8cm; align-items: baseline;
    font-size: 11pt;
  }
  .sheet-info strong { font-family: ui-monospace, monospace; font-size: 14pt; letter-spacing: 0.05em; }
  .sheet-info .meta { color: #777; margin-left: auto; font-family: ui-monospace, monospace; }
  .sheet-info .meta.warn { color: #b80056; }
  .crop {
    position: absolute;
    width: 0.5cm; height: 0.5cm;
    border-color: black; border-style: solid; border-width: 0;
  }
  .crop.tl { top: 0.75cm; left: 0.75cm; border-top-width: 0.5pt; border-left-width: 0.5pt; }
  .crop.tr { top: 0.75cm; right: 0.75cm; border-top-width: 0.5pt; border-right-width: 0.5pt; }
  .crop.bl { bottom: 0.75cm; left: 0.75cm; border-bottom-width: 0.5pt; border-left-width: 0.5pt; }
  .crop.br { bottom: 0.75cm; right: 0.75cm; border-bottom-width: 0.5pt; border-right-width: 0.5pt; }
  .piece-label {
    position: absolute;
    font-family: ui-monospace, monospace;
    font-size: 10pt;
    color: black;
    font-weight: 600;
    line-height: 1.2cm;
    padding-left: 0.15cm;
    border-bottom: 0.3pt dashed #aaa;
    box-sizing: border-box;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .piece-label .muted { color: #777; font-weight: 400; font-size: 9pt; margin-left: 0.4cm; }
  .piece-on-sheet { position: absolute; }
  .piece-on-sheet svg { display: block; }
  .piece-on-sheet svg line, .piece-on-sheet svg path { fill: none; stroke: black; stroke-width: 1.4; vector-effect: non-scaling-stroke; stroke-linecap: round; stroke-linejoin: round; }
  .piece-on-sheet svg .kind-dart { stroke: #b80056; stroke-dasharray: 3 2; stroke-width: 1.2; }
  .piece-on-sheet svg .kind-fold { stroke: #555; stroke-dasharray: 6 3; stroke-width: 1.0; }
  .piece-on-sheet svg .kind-reference { stroke: #888; stroke-width: 0.7; opacity: 0.8; }
  .piece-on-sheet svg .point-dot { fill: black; stroke: white; stroke-width: 1; vector-effect: non-scaling-stroke; }
  .piece-on-sheet svg .point-label { fill: black; font-family: monospace; paint-order: stroke; stroke: white; stroke-linejoin: round; }
  .piece-on-sheet svg .segment-length { fill: #444; font-family: monospace; text-anchor: middle; dominant-baseline: middle; paint-order: stroke; stroke: white; stroke-linejoin: round; }
  /* segment-lengths group must not inherit the segment line/path styles */
  .piece-on-sheet svg .segment-lengths text { fill: #444; stroke: white; }

  @media screen {
    body { padding: 1rem; }
    .info {
      max-width: 700px; margin: 0 auto 1.5rem;
      padding: 1rem; border: 1px solid #ddd; border-radius: 6px; background: #fafafa;
    }
    .info h1 { font-size: 1.1rem; margin: 0 0 0.4rem; }
    .info p { margin: 0.4rem 0; font-size: 0.9rem; line-height: 1.5; }
    .info button { margin-top: 0.6rem; padding: 0.5rem 1rem; cursor: pointer; font-size: 0.95rem; }
    .sheet-wrap {
      width: 25.23cm;   /* 84.1 * 0.30 */
      height: 35.67cm;  /* 118.9 * 0.30 */
      overflow: hidden;
      margin: 0 auto 1.5rem;
      box-shadow: 0 2px 6px rgba(0,0,0,0.15);
      background: white;
    }
    .sheet-wrap .sheet {
      transform: scale(0.30);
      transform-origin: top left;
    }
  }
  @media print {
    .info { display: none; }
    body { padding: 0; }
    .sheet-wrap {
      width: auto; height: auto;
      overflow: visible;
      margin: 0;
      box-shadow: none;
      page-break-after: always;
    }
    .sheet-wrap:last-child { page-break-after: auto; }
    .sheet-wrap .sheet { transform: none; }
  }
</style>
</head>
<body>
<div class="info">
  <h1>${escapeXml(projectName)} — Stampa A0 (${totalSheets} ${totalSheets === 1 ? 'foglio' : 'fogli'})</h1>
  <p>Formato A0 portrait (84.1 × 118.9 cm). Pezzi totali: <strong>${valid.length}</strong>${tiledOversize.length > 0 ? ` · <strong>${tiledOversize.length}</strong> pezzo${tiledOversize.length === 1 ? '' : 'i'} più grande di un singolo A0 — suddiviso su più fogli (codici <code>A1, A2…</code>)` : ''}.</p>
  <p>In stamperia: imposta margini <strong>Nessuno</strong> e ridimensionamento <strong>100%</strong> per ottenere la scala 1:1. Salva come PDF e invia il file. Le pagine assemblate seguendo i codici riproducono il pezzo a misura.</p>
  <button onclick="window.print()">Stampa</button>
</div>
${sheetsHtml.join('\n')}
<script>window.addEventListener('load',function(){setTimeout(function(){window.print();},500);});</script>
</body>
</html>`

  openHtmlInNewTab(html)
}
